import { load } from '@apple/mapkit-loader'
import { useEffect, useRef, useState } from 'react'

/**
 * Die Lage eines eingereichten Ortes — auf Apple Maps, mit Luftbild.
 *
 * **Warum überhaupt eine richtige Karte?** Bei einer Einreichung ist die
 * Frage nicht „wo ungefähr", sondern „ist das dort wirklich ein Spielplatz".
 * Auf einem Straßenplan ist eine Wiese eine Wiese; auf dem Luftbild sieht man
 * Schaukelgerüst, Sandfläche und Zaun. Die eingebettete Straßenkarte, die hier
 * bis zum 30.09.2026 stand, konnte das nicht — OpenStreetMap liefert keine
 * Luftbilder.
 *
 * **Luftbild heißt hier „hybrid"**: Satellitenbild *mit* Straßen und Namen.
 * Reines Satellitenbild sieht besser aus und hilft schlechter — ohne
 * Straßennamen ist auf 50 Metern Genauigkeit nicht zu sagen, ob das die
 * gemeinte Adresse ist.
 *
 * **Der Schlüssel ist auf `*.kinderspiel.app` beschränkt.** Das ist Absicht
 * und bei MapKit JS der vorgesehene Weg: Das Token liegt im Browser, denn der
 * Browser lädt die Karte. Geschützt wird es nicht durch Geheimhaltung,
 * sondern dadurch, dass es nur auf unseren Domains gilt.
 *
 * Woraus folgt: **Auf `localhost` und auf `kinderspiel-admin.web.app`
 * funktioniert es nicht.** Dort — und wenn gar kein Token gesetzt ist — fällt
 * die Ansicht auf die alte OpenStreetMap-Einbettung zurück, mit einem Satz,
 * der sagt warum. Eine leere Fläche wäre die schlechtere Antwort.
 */

const SCHLUESSEL = import.meta.env['VITE_MAPKIT_TOKEN'] as string | undefined

/** Die Wahl bleibt über Einreichungen hinweg stehen — es werden viele. */
const MERKER = 'ortskarte-art'

type Art = 'karte' | 'luftbild'

function gemerkteArt(): Art {
  try {
    return localStorage.getItem(MERKER) === 'luftbild' ? 'luftbild' : 'karte'
  } catch {
    // Privates Fenster, gesperrte Speicherung: Dann eben jedes Mal neu.
    return 'karte'
  }
}

function merke(art: Art): void {
  try {
    localStorage.setItem(MERKER, art)
  } catch {
    // Eine vergessene Vorliebe ist kein Fehler, der jemanden aufhalten darf.
  }
}

/**
 * MapKit wird **einmal je Seite** geladen, nicht je Karte. Der Loader liefert
 * dasselbe Versprechen zurück, aber ein eigener Merker macht sichtbar, dass
 * das Absicht ist — und hält den Fehlschlag fest, statt ihn zu wiederholen.
 */
let geladen: Promise<Awaited<ReturnType<typeof load>>> | null = null

/**
 * Ein abgelehntes Token meldet sich **nicht** über ein abgelehntes
 * Versprechen.
 *
 * `load()` löst auf, die Karte lässt sich anlegen — und erst danach kommt ein
 * `error`-Ereignis mit `status: 'Unauthorized'`. Wer nur das Versprechen
 * abfängt, zeigt einen leeren grauen Kasten und merkt nichts. Nachgeprüft am
 * 30.09.2026 auf `localhost`, wo das Token nicht gilt:
 *
 *     [MapKit] Initialization failed because the authorization token is
 *     invalid. Origin does not match — expected: *.kinderspiel.app,
 *     actual: http://localhost:5181
 *
 * Deshalb hört hier jemand zu und sagt allen Karten Bescheid.
 */
let abgelehnt: string | null = null
const zuhoerer = new Set<(grund: string) => void>()

function meldeAblehnung(grund: string): void {
  abgelehnt = grund
  for (const h of zuhoerer) h(grund)
}

function mapkit() {
  if (!SCHLUESSEL) return Promise.reject(new Error('kein Token'))
  geladen ??= load({
    token: SCHLUESSEL,
    language: 'de-DE',
    // Nur was gebraucht wird: keine Suche, keine Routen, kein Look Around.
    libraries: ['map', 'annotations'],
  }).then((mk) => {
    mk.addEventListener('error', (e) => {
      const { status, message } = e as Event & { status?: string; message?: string }
      // `Unauthorized` ist die falsche Domain oder ein zurückgezogenes Token;
      // `Too Many Requests` wäre das Tageskontingent. Beides endet in
      // derselben leeren Fläche, also in derselben Auffanglösung.
      meldeAblehnung(message ?? status ?? 'Unauthorized')
    })
    return mk
  })
  return geladen
}

type MapkitRaum = Awaited<ReturnType<typeof load>>
type Karte = InstanceType<MapkitRaum['Map']>

const artZu = (mk: MapkitRaum, art: Art) =>
  art === 'luftbild' ? mk.MapType.Hybrid : mk.MapType.Standard

export default function Ortskarte({
  lat,
  lon,
  name,
}: {
  lat: number
  lon: number
  name?: string | null
}) {
  const behaelter = useRef<HTMLDivElement | null>(null)
  const karte = useRef<Karte | null>(null)
  const [art, setArt] = useState<Art>(gemerkteArt)
  const [fehler, setFehler] = useState<string | null>(
    SCHLUESSEL ? abgelehnt : 'kein_token',
  )

  // Die Ablehnung kann nach dem Aufbau eintreffen — und für eine Karte, die
  // längst dasteht. Dann tauscht dieser Zustand sie gegen die Straßenkarte.
  useEffect(() => {
    const h = (grund: string) => setFehler(grund)
    zuhoerer.add(h)
    return () => {
      zuhoerer.delete(h)
    }
  }, [])

  // Die Art beim Aufbau kommt aus einer Ref, nicht aus der Abhängigkeitsliste:
  // Sonst baute jeder Wechsel die Karte neu auf und setzte den Ausschnitt
  // zurück — wer hineingezoomt hat, verlöre die Stelle, die er ansieht.
  const artBeimAufbau = useRef(art)
  artBeimAufbau.current = art

  useEffect(() => {
    const ziel = behaelter.current
    if (!ziel) return
    let abgebrochen = false

    void mapkit()
      .then((mk) => {
        if (abgebrochen) return
        const mitte = new mk.Coordinate(lat, lon)
        karte.current = new mk.Map(ziel, {
          mapType: artZu(mk, artBeimAufbau.current),
          region: new mk.CoordinateRegion(mitte, new mk.CoordinateSpan(0.004, 0.004)),
          showsMapTypeControl: false,
          showsZoomControl: true,
          showsCompass: mk.FeatureVisibility.Adaptive,
          showsScale: mk.FeatureVisibility.Adaptive,
        })
        karte.current.addAnnotation(
          new mk.MarkerAnnotation(mitte, { title: name ?? 'Eingereichter Ort' }),
        )
      })
      .catch((f: unknown) => {
        if (abgebrochen) return
        setFehler(f instanceof Error ? f.message : 'unbekannt')
      })

    return () => {
      abgebrochen = true
      karte.current?.destroy()
      karte.current = null
    }
  }, [lat, lon, name])

  // Der Wechsel setzt eine Eigenschaft an der bestehenden Karte.
  useEffect(() => {
    if (!karte.current) return
    void mapkit().then((mk) => {
      if (karte.current) karte.current.mapType = artZu(mk, art)
    })
  }, [art])

  if (fehler) return <Ersatzkarte lat={lat} lon={lon} grund={fehler} />

  return (
    <div className="ortskarte">
      <div className="ortskarte-wahl" role="group" aria-label="Kartenart">
        {(['karte', 'luftbild'] as const).map((a) => (
          <button
            key={a}
            type="button"
            className={a === art ? 'knopf knopf-still aktiv' : 'knopf knopf-still'}
            aria-pressed={a === art}
            onClick={() => {
              setArt(a)
              merke(a)
            }}
          >
            {a === 'karte' ? 'Karte' : 'Luftbild'}
          </button>
        ))}
      </div>
      <div className="ortskarte-flaeche" ref={behaelter} />
    </div>
  )
}

/**
 * Die alte Einbettung als Auffanglösung.
 *
 * Sie stand hier jahrelang und tut, was sie kann: zeigen, wo ungefähr. Für
 * `localhost` ist das ohnehin alles, was geht — das Token gilt dort nicht.
 */
function Ersatzkarte({
  lat,
  lon,
  grund,
}: {
  lat: number
  lon: number
  grund: string
}) {
  const spanne = 0.004
  const kasten = [lon - spanne, lat - spanne, lon + spanne, lat + spanne].join(',')
  const quelle =
    `https://www.openstreetmap.org/export/embed.html?bbox=${kasten}` +
    `&layer=mapnik&marker=${lat},${lon}`

  return (
    <div className="karte-rahmen">
      <p className="leise">
        {grund === 'kein_token'
          ? 'Kein MapKit-Schlüssel gesetzt — Straßenkarte statt Luftbild.'
          : 'Apple Maps lädt hier nicht (der Schlüssel gilt nur auf ' +
            'kinderspiel.app) — Straßenkarte statt Luftbild.'}
      </p>
      <iframe
        title="Lage des eingereichten Ortes"
        src={quelle}
        loading="lazy"
        referrerPolicy="no-referrer"
      />
      <a
        href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=18/${lat}/${lon}`}
        target="_blank"
        rel="noreferrer noopener"
      >
        Größer auf OpenStreetMap ansehen
      </a>
    </div>
  )
}
